/**
 * Tile Paradise - Main Theme JS
 * Written in modern ES6, no external dependencies (like jQuery).
 */

document.addEventListener('DOMContentLoaded', () => {
  initAnnounce();
  initHeader();
  initMobileMenu();
  initHotspots();
  initHeroSlider();
  initReveal();
  initCart();
  initNewsletter();
  initFAQ();
  initProductGallery();
  initCRO();
  initProductAccordion();
  initQuantity();
  initVariantSelection();
  initStickyATC();
  initATCLoading();
  initFlashSale();
});

/* ============================================================================
   1. Announcement Bar
============================================================================ */
function initAnnounce() {
  const closeBtn = document.querySelector('.announce__close');
  const bar = document.querySelector('.announce, .hyper-announcement');
  
  if (!closeBtn || !bar) return;

  if (sessionStorage.getItem('announce-hidden')) {
    bar.style.display = 'none';
    return;
  }

  closeBtn.addEventListener('click', () => {
    bar.style.display = 'none';
    sessionStorage.setItem('announce-hidden', 'true');
  });
}

/* ============================================================================
   2. Header Scroll
============================================================================ */
function initHeader() {
  const header = document.querySelector('.header, .hyper-header');
  if (!header) return;

  window.addEventListener('scroll', () => {
    if (window.pageYOffset > 50) {
      header.classList.add('header--scrolled');
    } else {
      header.classList.remove('header--scrolled');
    }
  }, { passive: true });
}

/* ============================================================================
   2b. Mobile Menu
============================================================================ */
function initMobileMenu() {
  const openBtn = document.getElementById('mobile-menu-open');
  const closeBtn = document.getElementById('mobile-menu-close');
  const menu = document.getElementById('mobile-menu');
  const overlay = document.getElementById('mobile-menu-overlay');

  if (!openBtn || !menu || !overlay) return;

  function openMenu() {
    menu.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu() {
    menu.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  openBtn.addEventListener('click', openMenu);
  if (closeBtn) closeBtn.addEventListener('click', closeMenu);
  overlay.addEventListener('click', closeMenu);
  
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu();
  });
}

/* ============================================================================
   3. Hotspots
============================================================================ */
function initHotspots() {
  const dots = document.querySelectorAll('.hotspot__dot');
  if (!dots.length) return;

  dots.forEach(dot => {
    dot.addEventListener('click', (e) => {
      e.stopPropagation();
      const popup = dot.nextElementSibling;
      const isActive = dot.classList.contains('hotspot__dot--active');

      // Close all first
      document.querySelectorAll('.hotspot__dot--active').forEach(el => el.classList.remove('hotspot__dot--active'));
      document.querySelectorAll('.hotspot__popup--visible').forEach(el => el.classList.remove('hotspot__popup--visible'));

      if (!isActive && popup) {
        dot.classList.add('hotspot__dot--active');
        popup.classList.add('hotspot__popup--visible');
      }
    });
  });

  document.addEventListener('click', () => {
    document.querySelectorAll('.hotspot__dot--active').forEach(el => el.classList.remove('hotspot__dot--active'));
    document.querySelectorAll('.hotspot__popup--visible').forEach(el => el.classList.remove('hotspot__popup--visible'));
  });
}

/* ============================================================================
   4. Hero Slider
============================================================================ */
function initHeroSlider() {
  const slides = document.querySelectorAll('.hotspot-hero');
  const navDots = document.querySelectorAll('.hero-slider__dot');
  if (slides.length <= 1) return;

  let currentSlide = 0;
  let autoPlay;

  function showSlide(index) {
    slides.forEach((s, i) => {
      s.style.display = i === index ? 'block' : 'none';
    });
    navDots.forEach((d, i) => {
      d.classList.toggle('hero-slider__dot--active', i === index);
    });
    currentSlide = index;
  }

  function nextSlide() {
    showSlide((currentSlide + 1) % slides.length);
  }

  function startAutoPlay() {
    autoPlay = setInterval(nextSlide, 6000);
  }

  navDots.forEach((dot, index) => {
    dot.addEventListener('click', () => {
      clearInterval(autoPlay);
      showSlide(index);
      startAutoPlay();
    });
  });

  showSlide(0);
  startAutoPlay();
}

/* ============================================================================
   5. Scroll Reveal
============================================================================ */
function initReveal() {
  const elements = document.querySelectorAll('.reveal');
  if (!elements.length || typeof IntersectionObserver === 'undefined') return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('reveal--visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  elements.forEach(el => observer.observe(el));
}

/* ============================================================================
   6. Shopify AJAX Cart System
============================================================================ */
const CartAPI = {
  get: () => fetch('/cart.js').then(res => res.json()),
  add: (formData) => fetch('/cart/add.js', { method: 'POST', body: formData }).then(res => res.json()),
  change: (id, qty) => fetch('/cart/change.js', { 
    method: 'POST', 
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: String(id), quantity: qty })
  }).then(res => res.json())
};

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function initCart() {
  const cartDrawer = document.getElementById('cart-drawer');
  const cartOverlay = document.getElementById('cart-overlay');
  const cartClose = document.getElementById('cart-close');
  const cartItemsEl = document.getElementById('cart-items');
  const cartTotalEl = document.getElementById('cart-total');
  
  if (!cartDrawer) return;

  // Open/Close logic
  function openCart() {
    cartDrawer.classList.add('cart-drawer--open');
    cartOverlay.classList.add('cart-overlay--open');
    document.body.style.overflow = 'hidden';
    refreshCart();
  }

  function closeCart() {
    cartDrawer.classList.remove('cart-drawer--open');
    cartOverlay.classList.remove('cart-overlay--open');
    document.body.style.overflow = '';
  }

  /* Expose globally so mobile nav and other scripts can use */
  window.openCart = openCart;
  window.closeCart = closeCart;
  window.refreshCart = refreshCart;

  cartOverlay.addEventListener('click', closeCart);
  if (cartClose) cartClose.addEventListener('click', closeCart);

  // Bind to header cart icons
  document.querySelectorAll('a[href="/cart"], .header__cart-icon, .hyper-header__cart-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      // e.preventDefault(); // If you want slide-out instead of /cart redirect
    });
  });

  // Listen for add-to-cart events
  document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const btn = form.querySelector('[type="submit"]');
      const originalText = btn ? btn.textContent : '';
      
      if (btn) btn.textContent = 'Adding...';

      CartAPI.add(formData).then(item => {
        showToast('Added ' + item.product_title + ' to cart');
        if (btn) {
          btn.textContent = 'Added ✓';
          setTimeout(() => btn.textContent = originalText, 2000);
        }
        openCart(); // Slide open drawer
      }).catch(err => {
        showToast('Error adding item');
        if (btn) btn.textContent = originalText;
      });
    });
  });

  function refreshCart() {
    CartAPI.get().then(cart => {
      renderCartUI(cart);
      updateHeaderCartCount(cart.item_count);
    });
  }

  function renderCartUI(cart) {
    if (!cartItemsEl) return;
    
    if (cart.item_count === 0) {
      cartItemsEl.innerHTML = `
        <div class="cart-empty" style="text-align:center;padding:2rem 1rem;">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="cart-empty__cta" style="display:inline-block;margin-top:1rem;padding:0.75rem 1.5rem;background:#111;color:#fff;text-decoration:none;font-weight:bold;border-radius:4px;">Shop Now →</a>
        </div>
      `;
      if (cartTotalEl) cartTotalEl.textContent = formatMoney(0);
      return;
    }

    let html = '';
    cart.items.forEach(item => {
      const imgUrl = item.image ? item.image.replace(/(\.[^.]*)$/, '_200x$1') : '';
      html += `
        <div class="cart-item" style="display:flex; gap:1rem; margin-bottom:1.5rem; padding-bottom:1.5rem; border-bottom:1px solid #eee;">
          ${imgUrl ? `<img src="${imgUrl}" alt="${item.title}" style="width:80px;height:80px;object-fit:cover;border-radius:4px;">` : ''}
          <div class="cart-item__info" style="flex:1;">
            <div style="font-weight:600;font-size:0.95rem;margin-bottom:0.25rem;">${item.product_title}</div>
            ${item.variant_title && !item.variant_title.includes('Default') ? `<div style="font-size:0.8rem;color:#666;margin-bottom:0.5rem;">${item.variant_title}</div>` : ''}
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <div style="display:flex;align-items:center;border:1px solid #ddd;border-radius:4px;">
                <button type="button" class="cart-item-qty" data-id="${item.key}" data-qty="${item.quantity - 1}" style="background:none;border:none;padding:0.2rem 0.5rem;cursor:pointer;">-</button>
                <span style="font-size:0.9rem;min-width:1.5rem;text-align:center;">${item.quantity}</span>
                <button type="button" class="cart-item-qty" data-id="${item.key}" data-qty="${item.quantity + 1}" style="background:none;border:none;padding:0.2rem 0.5rem;cursor:pointer;">+</button>
              </div>
              <div style="font-weight:600;">${formatMoney(item.final_line_price)}</div>
            </div>
            <button type="button" class="cart-item-qty" data-id="${item.key}" data-qty="0" style="background:none;border:none;color:#D9381E;font-size:0.8rem;padding:0;margin-top:0.5rem;cursor:pointer;text-decoration:underline;">Remove</button>
          </div>
        </div>
      `;
    });

    cartItemsEl.innerHTML = html;
    if (cartTotalEl) cartTotalEl.textContent = formatMoney(cart.total_price);

    // Bind quantity buttons
    cartItemsEl.querySelectorAll('.cart-item-qty').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        const qty = parseInt(btn.getAttribute('data-qty'));
        CartAPI.change(id, qty).then(updatedCart => {
          renderCartUI(updatedCart);
          updateHeaderCartCount(updatedCart.item_count);
        });
      });
    });
  }

  function updateHeaderCartCount(count) {
    document.querySelectorAll('.header__cart-count').forEach(badge => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

function showToast(msg) {
  const toast = document.getElementById('toast');
  if (!toast) return;
  toast.textContent = msg;
  toast.classList.add('toast--show');
  setTimeout(() => toast.classList.remove('toast--show'), 2500);
}

/* ============================================================================
   7. Newsletter
============================================================================ */
function initNewsletter() {
  const form = document.getElementById('newsletter-form');
  if (!form) return;
  
  form.addEventListener('submit', (e) => {
    // We do NOT prevent default. Let Shopify handle the POST.
    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.textContent = 'Sending...';
  });
}

/* ============================================================================
   8. FAQ Accordion
============================================================================ */
function initFAQ() {
  const questions = document.querySelectorAll('.faq__question');
  if (!questions.length) return;

  questions.forEach(btn => {
    btn.addEventListener('click', () => {
      const parent = btn.closest('.faq__item');
      if (!parent) return;
      
      const isOpen = parent.classList.contains('faq__item--open');
      
      // Close all
      document.querySelectorAll('.faq__item').forEach(item => {
        item.classList.remove('faq__item--open');
      });

      // Open clicked
      if (!isOpen) {
        parent.classList.add('faq__item--open');
      }
    });
  });
}

/* ============================================================================
   9. Product Gallery (Swipe + Zoom)
============================================================================ */
function initProductGallery() {
  const slider = document.getElementById('gallery-main-slider');
  const thumbs = document.querySelectorAll('.gallery__thumb, .gal__thumb');
  const prevBtn = document.getElementById('gal-prev');
  const nextBtn = document.getElementById('gal-next');
  const mainImgs = document.querySelectorAll('.gal__main-img');

  if (!slider || !thumbs.length) return;

  // Sync scroll to thumbs
  slider.addEventListener('scroll', () => {
    const slideWidth = slider.clientWidth;
    const index = Math.round(slider.scrollLeft / slideWidth);
    updateActiveThumb(index);
  }, { passive: true });

  // Thumb click
  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => {
      slider.scrollTo({ left: slider.clientWidth * i, behavior: 'smooth' });
    });
  });

  // Arrows
  if (prevBtn && nextBtn) {
    prevBtn.addEventListener('click', () => {
      const w = slider.clientWidth;
      let newLeft = slider.scrollLeft - w;
      if (newLeft < 0) newLeft = slider.scrollWidth - w;
      slider.scrollTo({ left: newLeft, behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', () => {
      const w = slider.clientWidth;
      let newLeft = slider.scrollLeft + w;
      if (newLeft >= slider.scrollWidth - 10) newLeft = 0;
      slider.scrollTo({ left: newLeft, behavior: 'smooth' });
    });
  }

  function updateActiveThumb(index) {
    thumbs.forEach((t, i) => {
      t.classList.toggle('gallery__thumb--active', i === index);
      t.classList.toggle('gal__thumb--active', i === index);
    });
  }

  // Zoom lightbox
  let lightboxEl = null;

  mainImgs.forEach(img => {
    img.addEventListener('click', () => {
      // Use Shopify image_url format rewrite if possible, else just use src
      const highRes = img.src.replace(/width=\d+/, 'width=2000').replace(/_\d+x\d+/, '');
      
      lightboxEl = document.createElement('div');
      lightboxEl.style.cssText = `
        position: fixed; top:0; left:0; width:100%; height:100%;
        background: rgba(255,255,255,0.98); z-index:9999;
        display:flex; align-items:center; justify-content:center;
        cursor:zoom-out;
      `;
      
      const lgImg = document.createElement('img');
      lgImg.src = highRes;
      lgImg.style.cssText = 'max-width:90vw; max-height:90vh; object-fit:contain; border-radius:12px; box-shadow:0 10px 40px rgba(0,0,0,0.1);';
      
      lightboxEl.appendChild(lgImg);
      document.body.appendChild(lightboxEl);
      document.body.style.overflow = 'hidden';

      lightboxEl.addEventListener('click', closeLightbox);
    });
  });

  function closeLightbox() {
    if (lightboxEl) {
      lightboxEl.remove();
      lightboxEl = null;
      document.body.style.overflow = '';
    }
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeLightbox();
  });
}

/* ============================================================================
   10. CRO Features (Live Visitors & Shipping Timer)
============================================================================ */
function initCRO() {
  // Fake Live Visitors
  const visEl = document.getElementById('live-visitors');
  if (visEl) {
    let count = Math.floor(Math.random() * (32 - 8 + 1) + 8);
    visEl.textContent = count;
    setInterval(() => {
      const change = Math.random() > 0.5 ? 1 : -1;
      count += change;
      if (count < 5) count = 5;
      if (count > 45) count = 45;
      visEl.textContent = count;
    }, 8000);
  }

  // Shipping Cutoff (3 PM local time)
  const timerEl = document.getElementById('cro-timer');
  const dateEl = document.getElementById('cro-date');
  if (timerEl && dateEl) {
    function updateTimer() {
      const now = new Date();
      let cutoff = new Date();
      cutoff.setHours(15, 0, 0, 0); // 3 PM
      
      if (now > cutoff) {
        cutoff.setDate(cutoff.getDate() + 1);
      }
      
      const diff = cutoff - now;
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      timerEl.textContent = `${h} hrs ${m} mins`;
    }
    
    updateTimer();
    setInterval(updateTimer, 60000);
    
    // Delivery Date estimate (+5 days)
    const del = new Date();
    del.setDate(del.getDate() + 5);
    const opts = { month: 'short', day: 'numeric' };
    dateEl.textContent = del.toLocaleDateString('en-US', opts);
  }
}

/* ============================================================================
   11. Product Accordion
============================================================================ */
function initProductAccordion() {
  const headers = document.querySelectorAll('.pdp-accordion__header, .acc__h');
  
  headers.forEach(h => {
    h.addEventListener('click', () => {
      const item = h.parentElement;
      const isOpen = item.classList.contains('acc__item--open');
      
      // Close all
      document.querySelectorAll('.pdp-accordion__item, .acc__item').forEach(el => {
        el.classList.remove('acc__item--open', 'pdp-accordion__item--open');
      });

      if (!isOpen) {
        item.classList.add('acc__item--open', 'pdp-accordion__item--open');
      }
    });
  });
}

/* ============================================================================
   12. Quantity Selector
============================================================================ */
function initQuantity() {
  document.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const dir = btn.getAttribute('data-dir');
      const wrap = btn.closest('.qty-wrap');
      const input = wrap ? wrap.querySelector('.qty-input') : null;
      if (!input) return;
      
      let val = parseInt(input.value) || 1;
      if (dir === 'plus' && val < 99) val++;
      else if (dir === 'minus' && val > 1) val--;
      
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

/* ============================================================================
   13. Variant Selection
============================================================================ */
function initVariantSelection() {
  const jsonScript = document.querySelector('script[type="application/json"][id^="ProductJson-"]');
  if (!jsonScript) return;
  
  let variants = [];
  try {
    variants = JSON.parse(jsonScript.textContent);
  } catch(e) { return; }

  const varBtns = document.querySelectorAll('.var-btn');
  const priceEl = document.querySelector('.pi__price');
  const compPriceEl = document.querySelector('#pi-compare-price');
  const inputId = document.querySelector('input[name="id"]');
  const addBtn = document.getElementById('pdp-add-btn');
  
  varBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.closest('.pi__vars');
      if (!group) return;
      
      group.querySelectorAll('.var-btn').forEach(b => b.classList.remove('var-btn--active'));
      btn.classList.add('var-btn--active');
      
      const optionIndex = group.getAttribute('data-index');
      if (optionIndex) {
        const idx = optionIndex.replace('option', '');
        const labelSpan = document.getElementById(`pi-var-selected-${idx}`);
        if (labelSpan) labelSpan.textContent = btn.getAttribute('data-value');
      }
      
      const selectedOptions = [];
      document.querySelectorAll('.pi__vars').forEach(g => {
        const activeBtn = g.querySelector('.var-btn--active');
        selectedOptions.push(activeBtn ? activeBtn.getAttribute('data-value') : null);
      });
      
      const variant = variants.find(v => {
        return v.options.every((opt, idx) => opt === selectedOptions[idx]);
      });

      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'UNAVAILABLE';
        }
        return;
      }

      if (inputId) {
        inputId.value = variant.id;
        inputId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      if (window.history.replaceState) {
        window.history.replaceState(null, null, '?variant=' + variant.id);
      }
      
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
      }
      if (compPriceEl) {
        if (variant.compare_at_price > variant.price) {
          compPriceEl.textContent = formatMoney(variant.compare_at_price);
          compPriceEl.style.display = 'inline';
        } else {
          compPriceEl.style.display = 'none';
        }
      }
      
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
      }
      
      window.dispatchEvent(new CustomEvent('variant:changed', { detail: variant }));
    });
  });
}

/* ============================================================================
   14. Sticky ATC
============================================================================ */
function initStickyATC() {
  const btn = document.getElementById('pdp-add-btn');
  const sticky = document.getElementById('sticky-atc');
  const stickyBtn = document.getElementById('sticky-atc-btn');
  const stickyPrice = document.getElementById('sticky-atc-price');
  
  if (!btn || !sticky || typeof IntersectionObserver === 'undefined') return;

  const obs = new IntersectionObserver(entries => {
    const isVisible = entries[0].isIntersecting;
    if (isVisible) {
      sticky.style.transform = 'translateY(100%)';
    } else {
      sticky.style.transform = 'translateY(0)';
    }
  });
  obs.observe(btn);

  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      btn.click(); // Trigger main form submission
    });
  }

  window.addEventListener('variant:changed', (e) => {
    if (stickyPrice) {
      stickyPrice.textContent = formatMoney(e.detail.price);
    }
  });
}

/* ============================================================================
   15. ATC Loading State (Fallback if JS Cart fails)
============================================================================ */
function initATCLoading() {
  // Handled inside initCart() via fetch
}

/* ============================================================================
   16. Flash Sale Countdown
============================================================================ */
function initFlashSale() {
  const timer = document.getElementById('flash-timer');
  if (!timer) return;

  const endStr = timer.getAttribute('data-end');
  let endDate;
  if (endStr) {
    endDate = new Date(endStr).getTime();
  } else {
    // Default to end of current day if no valid date
    const now = new Date();
    endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).getTime();
  }

  const int = setInterval(() => {
    const now = new Date().getTime();
    const distance = endDate - now;

    if (distance < 0) {
      clearInterval(int);
      timer.innerHTML = '<span style="font-size:1.5rem;font-weight:bold;">SALE ENDED</span>';
      return;
    }

    const d = Math.floor(distance / (1000 * 60 * 60 * 24));
    const h = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const m = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const s = Math.floor((distance % (1000 * 60)) / 1000);

    const pad = num => num.toString().padStart(2, '0');

    timer.innerHTML = `
      <div class="hyper-flash__timer-block"><div>${pad(d)}</div><small>Days</small></div><div class="hyper-flash__sep">:</div>
      <div class="hyper-flash__timer-block"><div>${pad(h)}</div><small>Hrs</small></div><div class="hyper-flash__sep">:</div>
      <div class="hyper-flash__timer-block"><div>${pad(m)}</div><small>Min</small></div><div class="hyper-flash__sep">:</div>
      <div class="hyper-flash__timer-block"><div>${pad(s)}</div><small>Sec</small></div>
    `;
  }, 1000);
}
